import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserFrame, PREVIEWS, ScaledFrame } from "@/components/landing/previews";
import { FadeUp, EASE } from "@/components/landing/cinematic";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DESCRIPTIONS = {
    dashboard: "Business health in 30 seconds.",
    "product-brain": "Every SKU, one intelligence layer.",
    "inventory-intelligence": "Predicts stockouts before they happen.",
    "warehouse-hub": "Bin-level receiving and dispatch.",
    "customer-brain": "LTV, patterns, receivables — ranked.",
    "supplier-brain": "Reliability scored, per supplier.",
    "finance-hub": "Cash-basis truth, every day.",
    "business-intelligence": "Composable analytics, whole business.",
    "yug-brain": "Ask anything. Grounded in live data.",
};

export default function Explorer() {
    const tabs = Object.entries(PREVIEWS);
    const tabKeys = tabs.map(([k]) => k);
    const [active, setActive] = useState("dashboard");
    const [dir, setDir] = useState(1);
    const [railHovered, setRailHovered] = useState(false);
    const Preview = PREVIEWS[active].component;
    const pillsRef = useRef(null);
    const activePillRef = useRef(null);

    const switchTo = (key) => {
        const currentIdx = tabKeys.indexOf(active);
        const nextIdx = tabKeys.indexOf(key);
        setDir(nextIdx >= currentIdx ? 1 : -1);
        setActive(key);
    };

    const step = (delta) => {
        const idx = tabKeys.indexOf(active);
        const next = tabKeys[Math.max(0, Math.min(tabKeys.length - 1, idx + delta))];
        if (next !== active) switchTo(next);
    };

    // Keep the active pill scrolled into view within the strip
    React.useEffect(() => {
        const pill = activePillRef.current;
        const strip = pillsRef.current;
        if (!pill || !strip) return;
        const pr = pill.getBoundingClientRect();
        const sr = strip.getBoundingClientRect();
        if (pr.left < sr.left + 16 || pr.right > sr.right - 16) {
            strip.scrollTo({ left: pill.offsetLeft - strip.clientWidth / 2 + pill.clientWidth / 2, behavior: "smooth" });
        }
    }, [active]);

    return (
        <section
            id="product"
            className="relative -mt-8 lg:-mt-12 rounded-t-[32px] lg:rounded-t-[56px] bg-[#f7f3ec] pt-20 lg:pt-32 pb-20 lg:pb-28 overflow-hidden"
            data-testid="product-explorer"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <FadeUp>
                        <div className="font-mono text-[11px] font-medium uppercase tracking-[0.26em] text-[#c2410c]">
                            Product explorer
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.08}>
                        <h2 className="mt-4 sm:mt-5 font-display text-[32px] sm:text-5xl lg:text-[54px] font-bold tracking-[-0.03em] text-[#1c1917] leading-[1.05]">
                            13 modules.{" "}
                            <span className="font-editorial font-medium text-[#59524a]">One operating system.</span>
                        </h2>
                    </FadeUp>
                </div>

                {/* Mobile: dedicated experience — pill selector + centered, swipeable preview.
                    The screenshot NEVER extends outside the viewport. If the preview is wider
                    than the frame, it scales down (with whitespace) rather than being cropped. */}
                <div className="mt-10 lg:hidden">
                    {/* Pill selector — full-bleed horizontal scroll with snap */}
                    <FadeUp delay={0.1} className="-mx-5 sm:-mx-6">
                        <div
                            ref={pillsRef}
                            className="snap-x-strip flex gap-2 overflow-x-auto px-5 sm:px-6 pb-2"
                            data-testid="product-showcase-tabs-mobile"
                            role="tablist"
                            aria-label="Product modules"
                        >
                            {tabs.map(([key, meta]) => {
                                const isActive = active === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        ref={isActive ? activePillRef : null}
                                        onClick={() => switchTo(key)}
                                        data-testid={`showcase-tab-${key}`}
                                        className={`relative inline-flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-[12.5px] font-medium whitespace-nowrap transition-colors ${
                                            isActive
                                                ? "border-[#1c1917] bg-[#1c1917] text-[#f7f3ec]"
                                                : "border-[#1c1917]/15 bg-white text-[#59524a] active:bg-[#efe8dc]"
                                        }`}
                                    >
                                        <meta.icon
                                            className={`h-3.5 w-3.5 shrink-0 ${
                                                isActive ? "text-[#f7f3ec]" : meta.ai ? "text-[#4353e4]" : "text-[#a29a8e]"
                                            }`}
                                            strokeWidth={1.6}
                                        />
                                        {meta.label}
                                        {meta.ai && (
                                            <span
                                                className={`text-[8.5px] font-bold uppercase tracking-widest ${
                                                    isActive ? "text-[#8b96f8]" : "text-[#4353e4]"
                                                }`}
                                            >
                                                AI
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </FadeUp>

                    {/* Preview frame — centered, padded, overflow hidden.
                        The screenshot is inside a fixed-ratio card and scales down to always fit. */}
                    <FadeUp delay={0.2}>
                        <div
                            className="relative mx-auto mt-6 w-full overflow-hidden rounded-2xl border border-[#1c1917]/10 bg-[#fbf7ef] p-4 sm:p-5 shadow-[0_18px_44px_-24px_rgba(28,25,23,0.25)]"
                            data-testid="explorer-mobile-frame"
                        >
                            {/* Prev / next chevrons — accessible fallback for swipe */}
                            <button
                                type="button"
                                aria-label="Previous module"
                                data-testid="explorer-mobile-prev"
                                onClick={() => step(-1)}
                                disabled={tabKeys.indexOf(active) === 0}
                                className="absolute left-1.5 top-1/2 z-20 -translate-y-1/2 rounded-full border border-[#1c1917]/10 bg-white/85 p-1.5 text-[#33302b] backdrop-blur transition disabled:opacity-30"
                            >
                                <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
                            </button>
                            <button
                                type="button"
                                aria-label="Next module"
                                data-testid="explorer-mobile-next"
                                onClick={() => step(1)}
                                disabled={tabKeys.indexOf(active) === tabKeys.length - 1}
                                className="absolute right-1.5 top-1/2 z-20 -translate-y-1/2 rounded-full border border-[#1c1917]/10 bg-white/85 p-1.5 text-[#33302b] backdrop-blur transition disabled:opacity-30"
                            >
                                <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
                            </button>

                            {/* Swipeable centered stage. drag constrained to X; snap on release */}
                            <motion.div
                                className="flex items-center justify-center"
                                drag="x"
                                dragConstraints={{ left: 0, right: 0 }}
                                dragElastic={0.18}
                                dragMomentum={false}
                                onDragEnd={(_, info) => {
                                    const threshold = 48;
                                    if (info.offset.x < -threshold || info.velocity.x < -300) step(1);
                                    else if (info.offset.x > threshold || info.velocity.x > 300) step(-1);
                                }}
                                data-testid="explorer-mobile-swipe"
                            >
                                <div className="w-full max-w-[720px]">
                                    <AnimatePresence mode="wait" custom={dir}>
                                        <motion.div
                                            key={active}
                                            custom={dir}
                                            initial={{ opacity: 0, x: 40 * dir }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -40 * dir }}
                                            transition={{ duration: 0.32, ease: EASE }}
                                            className="w-full"
                                            data-testid={`explorer-mobile-active-${active}`}
                                        >
                                            <div className="mx-auto w-full rounded-xl overflow-hidden border border-[#1c1917]/10 bg-white shadow-[0_10px_28px_-18px_rgba(28,25,23,0.25)]">
                                                {/* ScaledFrame keeps aspect-ratio and shrinks to fit. Never upscales. */}
                                                <ScaledFrame designWidth={820} designHeight={500}>
                                                    <BrowserFrame url={`yugos.app/app/${active}`}>
                                                        <Preview />
                                                    </BrowserFrame>
                                                </ScaledFrame>
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            </motion.div>

                            {/* Dot pagination */}
                            <div className="mt-4 flex items-center justify-center gap-1.5" data-testid="explorer-mobile-dots">
                                {tabKeys.map((k, i) => {
                                    const isActive = k === active;
                                    return (
                                        <button
                                            key={k}
                                            type="button"
                                            aria-label={`Go to ${PREVIEWS[k].label}`}
                                            onClick={() => switchTo(k)}
                                            className={`h-1.5 rounded-full transition-all ${
                                                isActive ? "w-5 bg-[#1c1917]" : "w-1.5 bg-[#1c1917]/25"
                                            }`}
                                            data-testid={`explorer-mobile-dot-${i}`}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* Caption below the frame */}
                        <div className="mt-4 flex items-start justify-between gap-4 px-1">
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={`m-desc-${active}`}
                                    initial={{ opacity: 0, x: -8 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 8 }}
                                    transition={{ duration: 0.28 }}
                                    className="text-[14px] font-medium leading-snug text-[#1c1917]"
                                    data-testid="explorer-description-mobile"
                                >
                                    {DESCRIPTIONS[active]}
                                </motion.p>
                            </AnimatePresence>
                            <p className="shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-[#a29a8e]">
                                Swipe · {tabKeys.indexOf(active) + 1}/{tabKeys.length}
                            </p>
                        </div>
                    </FadeUp>
                </div>

                {/* Desktop: 12-column layout with vertical rail + oversized preview */}
                <div className="mt-14 hidden lg:grid lg:grid-cols-12 gap-10 items-start">
                    <FadeUp delay={0.1} className="lg:col-span-3">
                        {/* Floating CTA — hints that the rail is interactive. Fades on hover. */}
                        <motion.div
                            className="mb-4 flex items-center gap-2 px-1 font-mono text-[14.5px] uppercase tracking-[0.14em] text-[#4353e4]"
                            data-testid="explorer-cta-hint"
                            animate={{
                                opacity: railHovered ? 0 : 1,
                                y: railHovered ? -4 : [0, -3, 0],
                            }}
                            transition={
                                railHovered
                                    ? { duration: 0.35, ease: EASE }
                                    : {
                                          opacity: { duration: 0.4 },
                                          y: { duration: 2.2, repeat: Infinity, ease: "easeInOut", repeatDelay: 1.4 },
                                      }
                            }
                        >
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#4353e4]" />
                            Click a module to explore
                            <span aria-hidden="true">→</span>
                        </motion.div>

                        <div
                            className="flex flex-col gap-1"
                            data-testid="product-showcase-tabs"
                            onMouseEnter={() => setRailHovered(true)}
                            onMouseLeave={() => setRailHovered(false)}
                        >
                            {tabs.map(([key, meta]) => {
                                const isActive = active === key;
                                return (
                                    <button
                                        key={key}
                                        onClick={() => setActive(key)}
                                        data-testid={`showcase-tab-desktop-${key}`}
                                        className="relative flex w-full items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-[13px]"
                                    >
                                        {isActive && (
                                            <motion.span
                                                layoutId="explorer-pill"
                                                className="absolute inset-0 rounded-xl bg-[#1c1917] shadow-[0_10px_28px_-12px_rgba(28,25,23,0.5)]"
                                                transition={{ type: "spring", stiffness: 380, damping: 34 }}
                                            />
                                        )}
                                        <meta.icon
                                            className={`relative z-10 h-4 w-4 shrink-0 transition-colors duration-200 ${
                                                isActive ? "text-[#f7f3ec]" : meta.ai ? "text-[#4353e4]" : "text-[#a29a8e]"
                                            }`}
                                            strokeWidth={1.5}
                                        />
                                        <span
                                            className={`relative z-10 font-medium transition-colors duration-200 ${
                                                isActive ? "text-[#f7f3ec]" : "text-[#59524a] hover:text-[#1c1917]"
                                            }`}
                                        >
                                            {meta.label}
                                        </span>
                                        {meta.ai && (
                                            <span
                                                className={`relative z-10 ml-auto text-[8.5px] font-bold uppercase tracking-widest ${
                                                    isActive ? "text-[#8b96f8]" : "text-[#4353e4]"
                                                }`}
                                            >
                                                AI
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#a29a8e] leading-relaxed">
                            + User management · Audit logs · Settings · Help
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.18} className="lg:col-span-9 lg:-mr-8 xl:-mr-14">
                        <div style={{ perspective: 1600 }}>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={active}
                                    initial={{ opacity: 0, y: 28, scale: 0.98, rotateX: 6 }}
                                    animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                                    exit={{ opacity: 0, y: -16, scale: 0.985 }}
                                    transition={{ duration: 0.45, ease: EASE }}
                                    style={{ transformStyle: "preserve-3d" }}
                                >
                                    <BrowserFrame
                                        url={`yugos.app/app/${active}`}
                                        className="shadow-[0_50px_110px_-40px_rgba(28,25,23,0.4)]"
                                    >
                                        <Preview />
                                    </BrowserFrame>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                        <div className="mt-5 flex items-center justify-between gap-4">
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={`desc-${active}`}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 10 }}
                                    transition={{ duration: 0.3 }}
                                    className="text-[14px] font-medium text-[#1c1917]"
                                    data-testid="explorer-description"
                                >
                                    {DESCRIPTIONS[active]}
                                </motion.p>
                            </AnimatePresence>
                            <p className="shrink-0 font-mono text-[10.5px] uppercase tracking-[0.14em] text-[#a29a8e]">
                                Real screens · sample data
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}
